import type { CompiledChart } from '../../songpack/index.js';

/**
 * Walks a compiled chart in time order, handing each event out exactly once.
 *
 * Two properties matter more than anything else here:
 *
 * 1. **It never mutates the chart.** The compiled chart keeps its events in
 *    *source* order and carries a separate `order` index sorted by time. The
 *    cursor walks `order`, so consuming a chart cannot reorder or otherwise
 *    disturb data that came out of a Song Pack.
 * 2. **A late frame loses nothing.** Consumption is driven by a *time*, not by a
 *    delta, so a frame that jumps from 4 s to 10 s hands out every event in
 *    between on that one frame rather than dropping five of them or spreading
 *    them over the following frames. This is what makes a stalled tab recover
 *    correctly instead of desynchronising.
 */
export class ChartCursor {
  private index = 0;

  constructor(private readonly chart: CompiledChart) {}

  /** How many events have been handed out. */
  get consumed(): number {
    return this.index;
  }

  get total(): number {
    return this.chart.meta.eventCount;
  }

  get remaining(): number {
    return this.total - this.index;
  }

  get exhausted(): boolean {
    return this.index >= this.total;
  }

  /** Time of the next event to be handed out, or null when there is none. */
  peekTime(): number | null {
    if (this.exhausted) return null;
    const eventIndex = this.chart.order[this.index];
    if (eventIndex === undefined) return null;
    return this.chart.events.time[eventIndex] ?? null;
  }

  /**
   * Hands out every event at or before `songTime`.
   *
   * `<=` rather than `<` matches Unity's `songTime >= events[next].time`, so an
   * event sitting exactly on the current time fires on this frame in both
   * runtimes. Events sharing a timestamp are all released together, in `order`.
   *
   * @returns how many events were consumed.
   */
  consumeUntil(songTime: number, sink: (eventIndex: number, eventTime: number) => void): number {
    let consumed = 0;
    while (this.index < this.total) {
      const eventIndex = this.chart.order[this.index];
      if (eventIndex === undefined) break;
      const time = this.chart.events.time[eventIndex];
      if (time === undefined || time > songTime) break;
      this.index += 1;
      consumed += 1;
      sink(eventIndex, time);
    }
    return consumed;
  }

  /** Rewinds to the start. Used when a song is restarted. */
  reset(): void {
    this.index = 0;
  }

  /**
   * Repositions the cursor so the next event is the first one after `songTime`,
   * without emitting anything. For seeking, which Phase 2 does not use but which
   * would otherwise tempt a caller to reach into `index`.
   */
  seekTo(songTime: number): void {
    this.index = 0;
    while (this.index < this.total) {
      const eventIndex = this.chart.order[this.index];
      if (eventIndex === undefined) break;
      const time = this.chart.events.time[eventIndex];
      if (time === undefined || time > songTime) break;
      this.index += 1;
    }
  }
}
